import { useRef, useState } from 'react';
import { useLastEmployeesImport, useUploadEmployees } from '@/lib/queries';
import type { EmployeeChange, EmployeesUploadResult } from '@/lib/types';
import { Avatar } from '@/components/Avatar';

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const REASON_LABELS: Record<string, string> = {
  domain_not_allowed: 'Dominio no permitido',
  missing_email: 'Sin email',
  missing_zoho_id: 'Sin ZOHO_LINK_ID',
  terminated: 'Con fecha de baja',
};

type Phase = 'idle' | 'preview' | 'applied';

function ChangeRow({
  e,
  tone,
}: {
  e: EmployeeChange;
  tone: 'add' | 'remove';
}) {
  const dot = tone === 'add' ? 'bg-emerald-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-3 border-b border-ink-800/40 px-4 py-2 last:border-b-0">
      <span className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
      <Avatar upn={e.email} fullName={e.full_name} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-ink-100">{e.full_name}</div>
        <div className="truncate text-xs text-ink-500">{e.email}</div>
      </div>
      <div className="hidden text-right text-xs text-ink-400 sm:block">
        {e.business_line ?? '—'}
      </div>
    </div>
  );
}

function ChangesList({
  title,
  items,
  tone,
  emptyHint,
}: {
  title: string;
  items: EmployeeChange[];
  tone: 'add' | 'remove';
  emptyHint: string;
}) {
  return (
    <div className="border border-ink-800 bg-ink-900/40">
      <header className="flex items-baseline justify-between border-b border-ink-800/60 px-4 py-2">
        <div className="label">{title}</div>
        <div
          className={`text-2xl font-light tabular-nums ${
            items.length === 0
              ? 'text-ink-600'
              : tone === 'add'
                ? 'text-emerald-400'
                : 'text-red-400'
          }`}
        >
          {items.length}
        </div>
      </header>
      {items.length === 0 ? (
        <div className="px-4 py-5 text-center text-xs text-ink-600">{emptyHint}</div>
      ) : (
        <div className="max-h-72 overflow-y-auto">
          {items.map((e) => (
            <ChangeRow key={e.zoho_id} e={e} tone={tone} />
          ))}
        </div>
      )}
    </div>
  );
}

function ResultSummary({ r }: { r: EmployeesUploadResult }) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <div className="border border-ink-800 bg-ink-900/40 px-4 py-3">
        <div className="label">Aceptados</div>
        <div className="mt-2 text-2xl font-light tabular-nums text-ink-100">
          {r.accepted}
        </div>
      </div>
      <div className="border border-ink-800 bg-ink-900/40 px-4 py-3">
        <div className="label">Rechazados</div>
        <div
          className={`mt-2 text-2xl font-light tabular-nums ${
            r.rejected ? 'text-amber-400' : 'text-ink-400'
          }`}
        >
          {r.rejected}
        </div>
        {Object.keys(r.rejected_reasons).length > 0 ? (
          <ul className="mt-2 space-y-0.5 text-[0.7rem] text-ink-500">
            {Object.entries(r.rejected_reasons).map(([k, v]) => (
              <li key={k} className="flex justify-between gap-2">
                <span>{REASON_LABELS[k] ?? k}</span>
                <span className="tabular-nums">{v}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className="border border-ink-800 bg-ink-900/40 px-4 py-3">
        <div className="label">Filiales</div>
        <div className="mt-2 flex flex-wrap gap-1">
          {r.business_lines_detected.map((bl) => (
            <span
              key={bl}
              className="border border-ink-700 px-1.5 py-0.5 text-[0.7rem] text-ink-200"
            >
              {bl}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CsvImportCard() {
  const last = useLastEmployeesImport(true);
  const previewMut = useUploadEmployees(true);
  const commitMut = useUploadEmployees(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<EmployeesUploadResult | null>(null);
  const [applied, setApplied] = useState<EmployeesUploadResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const reset = () => {
    setPhase('idle');
    setStagedFile(null);
    setPreview(null);
    setApplied(null);
    setErrorMsg(null);
  };

  const startPreview = (file: File) => {
    setErrorMsg(null);
    setApplied(null);
    setStagedFile(file);
    previewMut.mutate(file, {
      onSuccess: (r) => {
        setPreview(r);
        setPhase('preview');
      },
      onError: (e) => {
        setErrorMsg(e instanceof Error ? e.message : 'Error al subir');
        setStagedFile(null);
      },
    });
  };

  const confirm = () => {
    if (!stagedFile) return;
    setErrorMsg(null);
    commitMut.mutate(stagedFile, {
      onSuccess: (r) => {
        setApplied(r);
        setPhase('applied');
        setPreview(null);
        setStagedFile(null);
      },
      onError: (e) => {
        setErrorMsg(e instanceof Error ? e.message : 'Error al aplicar');
      },
    });
  };

  return (
    <section className="panel">
      <header className="flex flex-wrap items-baseline justify-between gap-4 border-b border-ink-800 px-6 py-4">
        <div>
          <div className="label">Empleados · Zoho People</div>
          <h2 className="mt-1 text-lg font-light text-ink-50">
            Sincronizar listado de empleados
          </h2>
        </div>
        <div className="text-right text-xs text-ink-500">
          <div className="tabular-nums">
            {last.data ? `${last.data.total_employees} en base` : '—'}
          </div>
          <div className="mt-0.5">
            Último import:{' '}
            <span className="text-ink-300">{fmtDateTime(last.data?.at ?? null)}</span>
            {last.data?.by_name ? (
              <span className="ml-1 text-ink-500">por {last.data.by_name}</span>
            ) : null}
          </div>
        </div>
      </header>

      <div className="px-6 py-5">
        {phase === 'idle' ? (
          <>
            <p className="mb-4 text-sm text-ink-400">
              Sube el CSV{' '}
              <span className="font-mono text-ink-300">Employee View</span> exportado
              desde Zoho People. Antes de aplicar nada verás un resumen de altas y bajas.
            </p>
            <label
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const f = e.dataTransfer.files[0];
                if (f) startPreview(f);
              }}
              className={`flex cursor-pointer flex-col items-center justify-center border border-dashed px-6 py-10 text-center transition-colors ${
                dragging
                  ? 'border-ink-400 bg-ink-900/40'
                  : 'border-ink-800 hover:border-ink-700'
              }`}
            >
              <input
                ref={fileInput}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) startPreview(f);
                  if (fileInput.current) fileInput.current.value = '';
                }}
              />
              <div className="text-sm text-ink-200">
                {previewMut.isPending
                  ? 'Analizando CSV…'
                  : 'Arrastra el CSV aquí o haz click para seleccionar'}
              </div>
              <div className="mt-2 text-xs text-ink-500">CSV · máx. 5 MB</div>
            </label>
          </>
        ) : null}

        {phase === 'preview' && preview ? (
          <>
            <div className="mb-5 flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <div className="label">Vista previa · sin aplicar</div>
                <h3 className="mt-1 text-base text-ink-100">
                  {stagedFile?.name}
                  <span className="ml-2 text-xs text-ink-500">
                    · {((stagedFile?.size ?? 0) / 1024).toFixed(1)} KB
                  </span>
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={reset}
                  disabled={commitMut.isPending}
                  className="border border-ink-700 px-4 py-1.5 text-xs text-ink-300 transition-colors hover:bg-ink-900 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirm}
                  disabled={commitMut.isPending}
                  className="border border-emerald-700 bg-emerald-900/30 px-4 py-1.5 text-xs text-emerald-300 transition-colors hover:bg-emerald-900/60 disabled:opacity-50"
                >
                  {commitMut.isPending ? 'Aplicando…' : 'Confirmar import'}
                </button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <ChangesList
                title={`Nuevas altas · ${preview.new_users.length}`}
                items={preview.new_users}
                tone="add"
                emptyHint="Sin altas — el CSV no introduce empleados nuevos"
              />
              <ChangesList
                title={`Bajas · ${preview.removed_users.length}`}
                items={preview.removed_users}
                tone="remove"
                emptyHint="Sin bajas — todos los empleados activos siguen presentes"
              />
            </div>

            <div className="mt-5">
              <ResultSummary r={preview} />
            </div>

            {preview.removed_users.length > 0 ? (
              <div className="mt-4 border border-amber-800/40 bg-amber-900/10 px-4 py-3 text-xs text-amber-200">
                Al confirmar, los {preview.removed_users.length} empleados de "Bajas" se
                marcarán como dados de baja con fecha de hoy. Sus datos históricos de uso
                de Copilot se conservan.
              </div>
            ) : null}
          </>
        ) : null}

        {phase === 'applied' && applied ? (
          <>
            <div className="mb-5 flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <div className="label">Aplicado</div>
                <h3 className="mt-1 text-base text-ink-100">
                  Import completado · {applied.new_users.length} altas,{' '}
                  {applied.terminated_applied} bajas
                </h3>
              </div>
              <button
                type="button"
                onClick={reset}
                className="border border-ink-700 px-4 py-1.5 text-xs text-ink-300 hover:bg-ink-900"
              >
                Hacer otro import
              </button>
            </div>
            <ResultSummary r={applied} />
          </>
        ) : null}

        {errorMsg ? (
          <div className="mt-4 border border-red-700/50 bg-red-900/20 px-4 py-3 text-sm text-red-300">
            {errorMsg}
          </div>
        ) : null}
      </div>
    </section>
  );
}
